import { Box, Button, Card, Flex, Heading, Span, Table, Text } from '@chakra-ui/react';
import { useGetSingleOrder } from '../../api/hooks/useOrders';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { dbTimeToUserTime, Loading, priceFormatter } from '@shared';
import { useEffect, useState } from 'react';
import { useMobileContext } from '@shared';
import { IoArrowBackOutline } from 'react-icons/io5';
import axiosInstance from '../../api/axiosInstance';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { IoDownloadOutline } from 'react-icons/io5';
import { useCredits } from '../../context/CreditsContext';
import { getVat } from '../credits/BuyCredits/creditUtils';
import Decimal from 'decimal.js';
import { Helmet } from 'react-helmet';

const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;
const ENV = import.meta.env.VITE_ENV as string;

export default function Order() {
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { isMobile } = useMobileContext();
	const [isDownloading, setIsDownloading] = useState<boolean>(false);
	const { refetch: refetchCredits } = useCredits();

	// On local env and fake paytrail payments, we need to call this api endpoint manually to actually add credits.
	async function handleLocalOrderSuccessCallback(): Promise<void> {
		const queryString = new URL(window.location.href).searchParams.toString();
		await axiosInstance.get(`/callback/paytrail/success?${queryString}`);
		refetchCredits();
	}

	useEffect(() => {
		const status = searchParams.get('checkout-status');

		if (status === 'fail') {
			navigate('/credits');
		}

		if (status === 'ok') {
			if (ENV === 'local') {
				handleLocalOrderSuccessCallback();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { id } = useParams();
	const orderID = id ? parseInt(id, 10) : undefined;

	const { data: order, isPending } = useGetSingleOrder(orderID as number, {
		isEnabled: !!orderID,
	});

	useEffect(() => {
		if (!isPending && !order) {
			navigate('/credits?tab=history');
		}
	}, [isPending, order, navigate]);

	async function downloadHtmlAsPdf(fileName: string) {
		setIsDownloading(true);
		setTimeout(async () => {
			const originalElement = document.getElementById('receiptArea');
			if (!originalElement) {
				throw new Error(`Element with id "${'receiptArea'}" not found`);
			}
			const padding = 20;
			const clone = originalElement.cloneNode(true) as HTMLElement;
			const wrapper = document.createElement('div');

			Object.assign(wrapper.style, {
				width: '1200px',
				position: 'absolute',
				top: '-9999px',
				left: '-9999px',
				overflow: 'hidden',
				padding: '0',
				margin: '0',
				zIndex: '-1',
			});

			wrapper.appendChild(clone);
			document.body.appendChild(wrapper);

			clone.style.width = '1200px';

			const canvas = await html2canvas(clone, {
				scale: 1,
				backgroundColor: '#ffffff',
			});

			document.body.removeChild(wrapper);

			const pdf = new jsPDF('p', 'pt', 'a4');
			const pdfWidth = pdf.internal.pageSize.getWidth();
			const pdfHeight = pdf.internal.pageSize.getHeight();

			const usablePdfWidth = pdfWidth - padding * 2;
			const usablePdfHeight = pdfHeight - padding * 2;

			let position = 0;

			const canvasPageHeight = (canvas.width * usablePdfHeight) / usablePdfWidth;

			const pageCanvas = document.createElement('canvas');
			pageCanvas.width = canvas.width;
			pageCanvas.height = canvasPageHeight;
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const pageCtx = pageCanvas.getContext('2d')!;

			while (position < canvas.height) {
				const sliceHeight = Math.min(canvasPageHeight, canvas.height - position);

				pageCanvas.height = sliceHeight;
				pageCtx.clearRect(0, 0, pageCanvas.width, sliceHeight);

				pageCtx.drawImage(canvas, 0, position, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

				const imgData = pageCanvas.toDataURL('image/jpeg', 0.8);

				const displayHeight = (sliceHeight * usablePdfWidth) / canvas.width;

				pdf.addImage(imgData, 'JPEG', padding, padding, usablePdfWidth, displayHeight);

				position += sliceHeight;

				if (position < canvas.height) {
					pdf.addPage();
				}
			}
			pdf.save(fileName);

			setIsDownloading(false);
		}, 500);
	}

	function getOrderFlexDirection(): 'row' | 'column' {
		if (isDownloading) {
			return 'row';
		}

		if (isMobile) {
			return 'column';
		}

		return 'row';
	}

	if (isPending) {
		return (
			<Flex justifyContent="center" alignItems="center" height="100vh">
				<Loading />
			</Flex>
		);
	}

	if (!order) {
		return null;
	}

	return (
		<>
			<Helmet>
				<meta name="robots" content="noindex, nofollow" />
			</Helmet>
			{isDownloading ? (
				<>
					<Flex top={0} left={0} zIndex={isDownloading ? 9 : 0} width="100%" height="100%" position="absolute" backgroundColor="ghostwhite">
						{isDownloading ? <Loading label={t('general.generating_pdf')} /> : null}
					</Flex>
				</>
			) : null}
			<Flex flexDirection={'column'} gap={5} width={'100%'} alignItems={'center'} justifyContent={'center'} backgroundColor="white">
				<Card.Root width={'100%'} maxWidth="1200px">
					<Card.Body display={'flex'} flexDirection={'row'} justifyContent={'space-between'} flexWrap={'wrap'}>
						<Flex justifyContent={'space-between'} width="100%" alignItems={'center'} marginBottom={4}>
							<Flex alignItems={'center'} gap={2}>
								<IoArrowBackOutline data-cy="goBackButton" size={22} cursor={'pointer'} onClick={() => navigate('/credits?tab=history')} />
								<Heading size="lg">{`${t('general.order')}`}</Heading>
							</Flex>
							<Button onClick={() => downloadHtmlAsPdf('receipt')}>
								{t('general.download_receipt')} <IoDownloadOutline style={{ marginTop: '-5px', width: '22px', height: '22px' }} size={25} />
							</Button>
						</Flex>
						<Box id="receiptArea" width="100%">
							<Flex flexDirection={getOrderFlexDirection()} gap={isMobile ? 4 : 0} justifyContent={'space-between'} width="100%">
								<Flex flexDirection={'column'}>
									<Heading marginBottom={2} size="lg">
										Tavoittaja
									</Heading>
									<Heading size="md">{t('general.receiver')}</Heading>
									<Text userSelect="text">Prospectum Oy</Text>
									<Text userSelect="text">Ylistönmäentie 24</Text>
									<Text userSelect="text">40500 Jyväskylä</Text>
									<Text userSelect="text">{t('general.business_id')}: 2353129-3</Text>
									<Text userSelect="text">{t('general.vat_code')}: FI23531293</Text>
									<Heading marginTop={2} size="md">{`${t('general.buy_date')} ${dbTimeToUserTime(order.timeCreated, TIMEZONE)}`}</Heading>
									<Heading marginTop={2} size="md">{`${t('general.buyer')} ${order.purchaserFirstName} ${order.purchaserLastName}`}</Heading>
								</Flex>
								<Flex flexDirection={'column'}>
									<Heading marginBottom={2} size="lg">{`${t('general.order_id')}  ${order.orderID}`}</Heading>
									<Heading size="md">{t('general.payer')}</Heading>
									<Text userSelect="text">{order.companyName}</Text>
									<Text userSelect="text">{order.postalAddress}</Text>
									<Text userSelect="text">
										{order.postalCode} {order.city}
									</Text>
									<Text userSelect="text">
										{t('general.business_id')}: {order.businessID}
									</Text>
									<Text userSelect="text">
										{t('general.vat_code')}: {order.vatCode}
									</Text>
								</Flex>
							</Flex>

							<Table.ScrollArea maxW="full" marginTop={10} width="100%">
								<Table.Root striped>
									<Table.Header>
										<Table.Row>
											<Table.ColumnHeader>{t('general.product')}</Table.ColumnHeader>
											<Table.ColumnHeader>{t('general.price_without_vat')}</Table.ColumnHeader>
											<Table.ColumnHeader>{t('general.vat')}</Table.ColumnHeader>
											<Table.ColumnHeader>{t('general.total')}</Table.ColumnHeader>
										</Table.Row>
									</Table.Header>
									<Table.Body maxH="full" overflowY="auto">
										{order.orderRows?.map(row => (
											<Table.Row key={row.orderRowID}>
												<Table.Cell>{row.name}</Table.Cell>
												<Table.Cell>
													{row.totalBeforeDiscountWithoutVat !== row.totalWithoutVat ? (
														<Span textDecor={row.totalBeforeDiscountWithoutVat ? 'line-through' : ''} marginRight="1">
															{priceFormatter(row.totalBeforeDiscountWithoutVat)}
														</Span>
													) : null}
													<Span>{priceFormatter(row.totalWithoutVat)}</Span>
												</Table.Cell>
												<Table.Cell>{priceFormatter(getVat(Number(row.totalWithoutVat)))}</Table.Cell>
												<Table.Cell>{priceFormatter(row.totalWithVat)}</Table.Cell>
											</Table.Row>
										))}
									</Table.Body>
								</Table.Root>
							</Table.ScrollArea>

							<Flex marginTop={10} flexDirection={'row'} justifyContent={'end'} gap={10} width="100%">
								<Flex flexDirection="column">
									<Text userSelect="text">{t('general.total_without_vat')}</Text>
									<Text userSelect="text">{t('general.vat')}</Text>
									<Text userSelect="text">{t('general.total')}</Text>
								</Flex>
								{order.totalWithVat && order.totalWithoutVat ? (
									<Flex flexDirection="column" textAlign={'end'} paddingBottom={4}>
										<Text userSelect="text">{priceFormatter(order.totalWithoutVatRounded || '')}</Text>
										<Text userSelect="text">
											{priceFormatter(new Decimal(Number(order.totalWithVatRounded) - Number(order.totalWithoutVatRounded)).toDecimalPlaces(2).toNumber())}
										</Text>
										<Text userSelect="text">{priceFormatter(order.totalWithVatRounded || 0)}</Text>
									</Flex>
								) : null}
							</Flex>
						</Box>
					</Card.Body>
				</Card.Root>
			</Flex>
		</>
	);
}
